import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { 
  Card, 
  CardHeader,
  CardBody, 
  Button, 
  OutlineButton, 
  Badge, 
  TableContainer, 
  Table, 
  TableHead, 
  TableRow, 
  TableCell, 
  TableHeader,
  FlexContainer,
  Input,
  Select,
  Pagination,
  PageButton
} from '../../styles/StyledComponents';
import { useAuth } from '../../context/AuthContext';
import { EmployeeLoan } from '../../types';
import apiService from '../../services/api';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const FilterSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.5rem;
  background-color: white;
  padding: 1rem;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  label {
    font-weight: 500;
    color: var(--dark-gray);
    white-space: nowrap;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
  max-width: 300px;
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`;

const AmountText = styled.span`
  font-weight: 500;
`;

const DateText = styled.span`
  color: var(--dark-gray);
  font-size: 0.85rem;
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--dark-gray);
`;

const Summary = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const SummaryCard = styled.div`
  background-color: white;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  padding: 1.5rem;
  text-align: center;
  
  h3 {
    margin: 0 0 0.5rem 0;
    color: var(--secondary-color);
    font-size: 1rem;
  }
  
  .amount {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--primary-color);
  }
  
  .label {
    font-size: 0.85rem;
    color: var(--dark-gray);
    margin-top: 0.5rem;
  }
`;

const LoansList: React.FC = () => {
  const { user } = useAuth();
  const [loans, setLoans] = useState<EmployeeLoan[]>([]);
  const [filteredLoans, setFilteredLoans] = useState<EmployeeLoan[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const itemsPerPage = 10;

  useEffect(() => {
    const loadLoansData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Load real employee loans from API
        const response = await apiService.getEmployeeLoans({ limit: 1000 });
        if (response.success && response.data) {
          setLoans(response.data);
          setFilteredLoans(response.data);
        } else {
          console.error('API response missing data:', response);
          setLoans([]);
          setFilteredLoans([]);
        }
      } catch (error: any) {
        console.error('Error loading loans data:', error);
        setError('Failed to load loans data');
        setLoans([]);
        setFilteredLoans([]);
      } finally {
        setIsLoading(false);
      }
    };
    
    if (user) {
      loadLoansData();
    }
  }, [user]);

  useEffect(() => {
    let result = [...loans];
    
    // Apply search filter
    if (searchTerm) {
      result = result.filter(
        loan => 
          loan.purpose.toLowerCase().includes(searchTerm.toLowerCase()) ||
          loan.employeeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          loan.id.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply status filter
    if (statusFilter) {
      result = result.filter(loan => loan.status === statusFilter);
    }
    
    // Apply type filter (employee role filter)
    if (typeFilter) {
      result = result.filter(loan => loan.employeeRole === typeFilter);
    }
    
    // Default sort by date (newest first)
    result.sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
    
    setFilteredLoans(result);
  }, [loans, searchTerm, statusFilter, typeFilter]);

  // Calculate pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredLoans.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredLoans.length / itemsPerPage);

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  const handleDeleteLoan = async (loanId: string) => {
    if (window.confirm('Are you sure you want to delete this loan?')) {
      try {
        const response = await apiService.deleteEmployeeLoan(loanId);
        if (response.success) {
          // Remove the loan from the list
          setLoans(prev => prev.filter(loan => loan.id !== loanId));
          setFilteredLoans(prev => prev.filter(loan => loan.id !== loanId));
        } else {
          setError(response.message || 'Failed to delete loan');
        }
      } catch (error: any) {
        console.error('Error deleting loan:', error);
        setError('Failed to delete loan');
      }
    }
  };

  const getStatusVariant = (status: string): 'success' | 'danger' | 'warning' | 'info' | 'primary' | 'secondary' => {
    switch (status) {
      case 'Pending': return 'warning';
      case 'Approved': return 'info';
      case 'Active': return 'primary';
      case 'Completed': return 'success';
      case 'Defaulted': return 'danger';
      case 'Cancelled': return 'secondary';
      default: return 'info';
    }
  };

  // Calculate summary statistics
  const totalPrincipal = loans.reduce((sum, loan) => sum + loan.principal, 0);
  const totalBalance = loans.reduce((sum, loan) => sum + loan.balance, 0);
  const totalPaid = loans.reduce((sum, loan) => sum + loan.paidAmount, 0);

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'TZS',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Check if current user can manage finances
  const canManageFinances = user?.role === 'MD' || user?.role === 'TD' || user?.role === 'Accountant';

  return (
    <div>
      <PageHeader>
        <h1 className="page-title">Employee Loans</h1>
        {canManageFinances && (
          <ActionButtons>
            <Button as={Link} to="/office/loans/new">
              + New Employee Loan
            </Button>
            <OutlineButton as={Link} to="/office">
              Finance
            </OutlineButton>
            <OutlineButton>
              Excel
            </OutlineButton>
            <OutlineButton>
              PDF
            </OutlineButton>
          </ActionButtons>
        )}
      </PageHeader>

      <Summary>
        <SummaryCard>
          <h3>Total Principal</h3>
          <div className="amount">{formatAmount(totalPrincipal)}</div>
          <div className="label">All loans</div>
        </SummaryCard>
        
        <SummaryCard>
          <h3>Total Balance</h3>
          <div className="amount">{formatAmount(totalBalance)}</div>
          <div className="label">Remaining to pay</div>
        </SummaryCard>
        
        <SummaryCard>
          <h3>Total Paid</h3>
          <div className="amount">{formatAmount(totalPaid)}</div>
          <div className="label">Principal + Interest</div>
        </SummaryCard>
      </Summary>

      <FilterSection>
        <SearchContainer>
          <Input 
            type="text" 
            placeholder="Search loans..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchContainer>

        <FilterGroup>
          <label>Status:</label>
          <Select 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="Pending">Pending</option>
            <option value="Approved">Approved</option>
            <option value="Active">Active</option>
            <option value="Completed">Completed</option>
            <option value="Defaulted">Defaulted</option>
            <option value="Cancelled">Cancelled</option>
          </Select>
        </FilterGroup>

        <FilterGroup>
          <label>Employee Role:</label>
          <Select 
            value={typeFilter} 
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="">All Roles</option>
            <option value="Supervisor">Supervisor</option>
            <option value="Project Manager">Project Manager</option>
            <option value="Store-Keeper (Logistics)">Store-Keeper (Logistics)</option>
            <option value="Cashier">Cashier</option>
            <option value="Procurement">Procurement</option>
            <option value="Accountant">Accountant</option>
          </Select>
        </FilterGroup>

        <Button onClick={() => {
          setSearchTerm('');
          setStatusFilter('');
          setTypeFilter('');
        }}>
          Reset Filters
        </Button>
      </FilterSection>

      <Card>
        <CardHeader>
          <h3>Loans ({filteredLoans.length})</h3>
        </CardHeader>
        <CardBody>
          {filteredLoans.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeader>ID</TableHeader>
                    <TableHeader>Purpose</TableHeader>
                    <TableHeader>Employee</TableHeader>
                    <TableHeader>Principal</TableHeader>
                    <TableHeader>Balance</TableHeader>
                    <TableHeader>Interest Rate</TableHeader>
                    <TableHeader>Duration</TableHeader>
                    <TableHeader>Status</TableHeader>
                    <TableHeader>Actions</TableHeader>
                  </TableRow>
                </TableHead>
                <tbody>
                  {currentItems.map((loan) => (
                    <TableRow key={loan.id}>
                      <TableCell>
                        <Link to={`/office/loans/${loan.id}`}>{loan.id}</Link>
                      </TableCell>
                      <TableCell>{loan.purpose}</TableCell>
                      <TableCell>{loan.employeeName}</TableCell>
                      <TableCell>
                        <AmountText>
                          {formatAmount(loan.principal)}
                        </AmountText>
                      </TableCell>
                      <TableCell>
                        <AmountText>
                          {formatAmount(loan.balance)}
                        </AmountText>
                      </TableCell>
                      <TableCell>{loan.interestRate}%</TableCell>
                      <TableCell>{loan.duration} months</TableCell>
                      <TableCell>
                        <Badge variant={getStatusVariant(loan.status)}>
                          {loan.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <FlexContainer gap="0.5rem">
                          <OutlineButton as={Link} to={`/office/loans/${loan.id}`} title="View Details">
                            View
                          </OutlineButton>
                          
                          {loan.status === 'Pending' && (
                            <OutlineButton as={Link} to={`/office/loans/edit/${loan.id}`} title="Edit Loan">
                              Edit
                            </OutlineButton>
                          )}
                          
                          {loan.status === 'Pending' && (
                            <OutlineButton 
                              onClick={() => handleDeleteLoan(loan.id)}
                              style={{ color: 'var(--danger-color)', borderColor: 'var(--danger-color)' }}
                              title="Delete Loan"
                            >
                              Delete
                            </OutlineButton>
                          )}
                          
                          {canManageFinances && loan.status === 'Active' && (
                            <OutlineButton as={Link} to={`/office/loans/${loan.id}/payment`} title="Make Payment">
                              Pay
                            </OutlineButton>
                          )}
                        </FlexContainer>
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            </TableContainer>
          ) : (
            <NoDataMessage>
              <p>No loans found matching your filters.</p>
            </NoDataMessage>
          )}

          {totalPages > 1 && (
            <Pagination>
              <PageButton 
                onClick={() => paginate(currentPage - 1)} 
                disabled={currentPage === 1}
              >
                &laquo;
              </PageButton>
              
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
                <PageButton
                  key={number}
                  active={number === currentPage}
                  onClick={() => paginate(number)}
                >
                  {number}
                </PageButton>
              ))}
              
              <PageButton 
                onClick={() => paginate(currentPage + 1)} 
                disabled={currentPage === totalPages}
              >
                &raquo;
              </PageButton>
            </Pagination>
          )}
        </CardBody>
      </Card>

      <div style={{ textAlign: 'right', padding: '1rem 0', color: 'var(--dark-gray)', fontSize: '0.8rem' }}>
        Developed by Sandfish
      </div>
    </div>
  );
};

export default LoansList; 